import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit2, Trash2, ExternalLink, Github } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useUserProjects, UserProject } from '@/hooks/useUserProjects';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';
import { ProjectForm } from './ProjectForm';

interface ProjectsSectionProps {
  userId?: string;
  isEditing?: boolean;
}

export const ProjectsSection = ({ userId, isEditing = false }: ProjectsSectionProps) => {
  const { t, i18n } = useTranslation('ui');
  const { projects, loading, addProject, updateProject, deleteProject } = useUserProjects(userId);
  const toast = useTranslatedToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    url: '',
    github_url: '',
    technologies: [] as string[]
  });

  const resetForm = useCallback(() => {
    setFormData({
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      url: '',
      github_url: '',
      technologies: []
    });
  }, []);

  const loadFormData = useCallback((project: UserProject) => {
    setFormData({
      title: project.title || '',
      description: project.description || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      url: project.url || '',
      github_url: project.github_url || '',
      technologies: project.technologies || []
    });
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const dataToSave = {
        ...formData,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        url: formData.url || null,
        github_url: formData.github_url || null,
        description: formData.description || null
      };

      if (editingId) {
        await updateProject(editingId, dataToSave);
        toast.success('profile_sections.project_updated');
        setEditingId(null);
      } else {
        await addProject(dataToSave);
        toast.success('profile_sections.project_added');
        setShowAddForm(false);
      }
      resetForm();
    } catch (err) {
      toast.error('profile_sections.project_save_failed');
    }
  }, [formData, editingId, updateProject, addProject, toast, resetForm]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteProject(id);
      toast.success('profile_sections.project_deleted');
    } catch (err) {
      toast.error('profile_sections.project_delete_failed');
    }
  }, [deleteProject, toast]);

  const handleTechnologyKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const value = e.currentTarget.value.trim();
      if (value && !formData.technologies.includes(value)) {
        setFormData(prev => ({
          ...prev,
          technologies: [...prev.technologies, value]
        }));
        e.currentTarget.value = '';
      }
    }
  }, [formData.technologies]);

  const removeTechnology = useCallback((tech: string) => {
    setFormData(prev => ({
      ...prev,
      technologies: prev.technologies.filter(t => t !== tech)
    }));
  }, []);

  const handleFormDataChange = useCallback((field: string, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCancel = useCallback(() => {
    if (editingId) {
      setEditingId(null);
    } else {
      setShowAddForm(false);
    }
    resetForm();
  }, [editingId, resetForm]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    
    // 根据当前语言设置本地化格式
    const languageMap: { [key: string]: string } = {
      'en': 'en-US',
      'es': 'es-ES', 
      'zh': 'zh-CN',
      'fr': 'fr-FR',
      'ja': 'ja-JP'
    };
    
    const locale = languageMap[i18n.language] || 'en-US';
    
    return new Date(dateString).toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">{t('profile_sections.loading_projects')}</div>;
  }


  return (
    <div className="space-y-4">
      {isEditing && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddForm(true)}
            disabled={showAddForm || editingId !== null}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('profile_sections.add_project')}
          </Button>
        </div>
      )}

      {(showAddForm || editingId) && (
        <ProjectForm
          formData={formData}
          onFormDataChange={handleFormDataChange}
          onSave={handleSave}
          onCancel={handleCancel}
          onTechnologyKeyDown={handleTechnologyKeyDown}
          onRemoveTechnology={removeTechnology}
          editingId={editingId || undefined}
        />
      )}

      {projects.length === 0 && !showAddForm ? (
        <div className="py-12 text-center">
          <Github className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">{t('profile_sections.no_projects_title')}</h3>
          <p className="text-muted-foreground text-sm">
            {t('profile_sections.no_projects_description')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((project) => {
            if (editingId === project.id) return null;

            return (
              <div key={project.id} className="border rounded-lg p-4">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Project title as primary heading with links */}
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-foreground">
                          {project.title}
                        </h3>
                        {project.url && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={project.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                        {project.github_url && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={project.github_url} target="_blank" rel="noopener noreferrer">
                              <Github className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                      
                      {/* Description as secondary text */}
                      {project.description && (
                        <p className="text-base text-muted-foreground mb-3">
                          {project.description}
                        </p>
                      )}
                      
                      {/* Tags for dates and technologies */}
                      <div className="flex items-center flex-wrap gap-2 text-xs mb-4">
                        {(project.start_date || project.end_date) && (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                            {formatDate(project.start_date)} - {project.end_date ? formatDate(project.end_date) : t('profile_sections.present')}
                          </span>
                        )}
                        {project.technologies && project.technologies.map((tech) => (
                          <span key={tech} className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {isEditing && (
                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingId(project.id);
                            loadFormData(project);
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(project.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};